// ─── widgets/section.js ───────────────────────────────────────────────────────
// Builds Section + Column widget pairs from <section> / <footer> elements.

import shortid from 'shortid';
import { cs, isVisible, getAbsoluteRect } from '../utils/dom.js';
import { extractSectionBackground, NO_SHADOW } from '../utils/style.js';
import { SECTION, SECTION_COLUMN } from 'constants/widgetTypes.jsx';

/**
 * Resolves which column a child element belongs to.
 * Returns the column's uid and the section rect used as the coordinate origin.
 *
 * @param {Element} el
 * @param {Array}   sectionMap
 * @param {string}  fallbackUid
 * @returns {{ parentUid, offsetRect, mobileOffsetRect }}
 */
export function resolveParent(el, sectionMap, fallbackUid) {
    for (const { sectionElement, columnUid, sectionRect, mobileSectionRect } of sectionMap) {
        if (sectionElement.contains(el))
            return { parentUid: columnUid, offsetRect: sectionRect, mobileOffsetRect: mobileSectionRect || sectionRect };
    }
    return { parentUid: fallbackUid, offsetRect: { top: 0, left: 0 }, mobileOffsetRect: { top: 0, left: 0 } };
}

/**
 * Iterates all <section> and <footer> elements in the desktop document,
 * creates a Section widget + Column widget for each, and records the mapping
 * needed by child widget builders (sectionMap).
 *
 * FIX – Hero section Y offset:
 *   The first page frame contains both the menu and the hero section.
 *   Both share the same page-widget coordinate space whose origin is the top of
 *   the menu.  All sections use  rect.top − pageTop  as their Y.  For the FIRST
 *   section on the hero page we must shift Y down by the menu height so the
 *   section doesn't overlap the menu.
 *
 * @param {Document} desktopDoc
 * @param {Document} mobileDoc
 * @param {Map}      sectionPageMap   output of buildSectionPageMap
 * @param {string}   firstPageUid
 * @param {{ value: number }} orderRef  mutable order counter
 * @returns {{ sectionWidgets, columnWidgets, sectionMap }}
 */
export function buildSectionMap(desktopDoc, mobileDoc, sectionPageMap, firstPageUid, orderRef, narrowSectionEls = new Set()) {
    const sectionWidgets = [];
    const columnWidgets  = [];
    const sectionMap     = [];

    // Collect desktop candidates (deduplicated).
    // Include <header> tags ONLY when they are NOT a menu widget.
    // Include <section>, <footer>, and [data-widget="section"] as before.
    const seen       = new Set();
    const candidates = [
        ...Array.from(desktopDoc.querySelectorAll('section, footer')),
        ...Array.from(desktopDoc.querySelectorAll('header')).filter(
            (h) => h.getAttribute('data-widget') !== 'menu',
        ),
        ...Array.from(desktopDoc.querySelectorAll('[data-widget="section"]')),
    ]
        .sort((a, b) => {
            // Preserve DOM order after merging multiple querySelectors
            const pos = a.compareDocumentPosition(b);
            return pos & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
        })
        .filter((el) => { if (seen.has(el)) return false; seen.add(el); return true; });

    const mobileCandidates = [
        ...Array.from(mobileDoc.querySelectorAll('section, footer')),
        ...Array.from(mobileDoc.querySelectorAll('header')).filter(
            (h) => h.getAttribute('data-widget') !== 'menu',
        ),
        ...Array.from(mobileDoc.querySelectorAll('[data-widget="section"]')),
    ].sort((a, b) => {
        const pos = a.compareDocumentPosition(b);
        return pos & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });

    // Measure menu height for the hero-page Y-offset fix
    const desktopMenuEl = desktopDoc.querySelector('[data-widget="menu"]');
    const mobileMenuEl  = mobileDoc.querySelector('[data-widget="menu"]');
    const menuDesktopH  = desktopMenuEl ? Math.round(desktopMenuEl.getBoundingClientRect().height) : 0;
    const menuMobileH   = mobileMenuEl  ? Math.round(mobileMenuEl.getBoundingClientRect().height)  : 0;

    // Track the absolute doc-top of the first section per page
    // (used as the page's coordinate origin for relative Y values)
    const pageTopDesktopMap = {};
    const pageTopMobileMap  = {};

    candidates.forEach((el) => {
        if (!isVisible(el)) return;

        const rect        = el.getBoundingClientRect();
        const style       = cs(el);
        const isFullWidth = rect.width >= 1200;

        // ── Narrow-section rule ────────────────────────────────────────────────
        // Sections in narrowSectionEls were identified in buildSectionPageMap as
        // being < 1150px wide (nested layout cells like bento grid items).
        // They do NOT get their own Section/Column widget pair. Their children
        // are placed directly on the nearest ancestor page widget.
        const isNarrow = narrowSectionEls.has(el);

        const mobileEl = mobileCandidates.find(
            (m) => m.tagName === el.tagName && m.textContent.trim() === el.textContent.trim(),
        );
        const mRect = mobileEl
            ? mobileEl.getBoundingClientRect()
            : { top: rect.top, left: 0, width: 375, height: rect.height };

        const mapping   = sectionPageMap.get(el);
        const pageUid   = mapping?.pageUid ?? firstPageUid;
        const parentUid = pageUid;

        // Record the first FULL-WIDTH section top per page as the page origin.
        // Narrow sections are positioned relative to their page's own origin;
        // they must not override pageTopDesktopMap if a full-width section was
        // already recorded for this page.
        if (pageUid && !isNarrow && !(pageUid in pageTopDesktopMap)) pageTopDesktopMap[pageUid] = Math.round(rect.top);
        if (pageUid && !isNarrow && !(pageUid in pageTopMobileMap))  pageTopMobileMap[pageUid]  = Math.round(mRect.top);

        // Narrow sections still need a page-top origin for their own Y calc.
        // Fall back to their own rect.top if the page has no full-width section yet.
        if (pageUid && !(pageUid in pageTopDesktopMap)) pageTopDesktopMap[pageUid] = Math.round(rect.top);
        if (pageUid && !(pageUid in pageTopMobileMap))  pageTopMobileMap[pageUid]  = Math.round(mRect.top);

        const pageTopDesktop = pageTopDesktopMap[pageUid] ?? 0;
        const pageTopMobile  = pageTopMobileMap[pageUid]  ?? 0;

        // ── Y-offset fix for the first section on the HERO page ───────────────
        const isHeroPage    = pageUid === firstPageUid;
        const extraDesktopY = isHeroPage ? menuDesktopH : 0;
        const extraMobileY  = isHeroPage ? menuMobileH  : 0;

        const sectionY       = Math.round(rect.top)  - pageTopDesktop + extraDesktopY;
        const sectionMobileY = Math.round(mRect.top) - pageTopMobile  + extraMobileY;

        // ── Narrow section: no Section/Column widgets — children go on page ───
        // Walk the DOM upward to find the nearest wide ancestor that IS in
        // sectionPageMap — that ancestor's pageUid is the correct parent.
        if (isNarrow) {
            let ancestorPageUid = firstPageUid;
            let node = el.parentElement;
            while (node) {
                if (sectionPageMap.has(node)) { ancestorPageUid = sectionPageMap.get(node).pageUid; break; }
                node = node.parentElement;
            }

            // Use the page's own top as the coordinate origin so child positions
            // are relative to the page widget, not the narrow section's rect.
            const pageOriginDesktop = pageTopDesktopMap[ancestorPageUid] ?? Math.round(rect.top);
            const pageOriginMobile  = pageTopMobileMap[ancestorPageUid]  ?? Math.round(mRect.top);

            sectionMap.push({
                sectionElement:    el,
                sectionUid:        null,
                columnUid:         ancestorPageUid, // children parent = page widget
                sectionRect:       { top: pageOriginDesktop, left: 0, width: rect.width, height: rect.height },
                mobileSectionRect: { top: pageOriginMobile,  left: 0, width: mRect.width, height: mRect.height },
                isNarrow:          true,
            });
            return; // skip Section + Column widget creation
        }

        const bg  = extractSectionBackground(style, el, desktopDoc);
        const ord = orderRef.value++;

        const sUid = `widget_${shortid.generate()}`;
        const cUid = `widget_${shortid.generate()}`;

        sectionWidgets.push({
            uid:           sUid,
            type:          SECTION,
            name:          'Section',
            parent:        parentUid,
            order:         ord,
            systemDefined: false,
            fonts:         null,
            actions:       null,
            parameter:     null,
            properties: {
                desktop: {
                    visible:         true,
                    disableOptimize: false,
                    layout: {
                        transform: { translate: `0px,${sectionY}px`, rotate: '0deg', scale: '1,1' },
                        width:  isFullWidth ? '1440px' : `${Math.round(rect.width)}px`,
                        height: `${Math.round(rect.height)}px`,
                    },
                    stretched:    isFullWidth,
                    margin:       0,
                    padding:      0,
                    columnSpacing: 0,
                    background:   bg,
                    shadow:       NO_SHADOW,
                },
                mobile: {
                    visible:         true,
                    disableOptimize: false,
                    layout: {
                        transform: { translate: `0px,${sectionMobileY}px`, rotate: '0deg' },
                        width:  '375px',
                        height: `${Math.round(mRect.height)}px`,
                    },
                    stretched:    false,
                    margin:       0,
                    padding:      0,
                    columnSpacing: 0,
                    background:   null,
                    shadow:       NO_SHADOW,
                },
            },
            content: null,
        });

        columnWidgets.push({
            uid:           cUid,
            type:          SECTION_COLUMN,
            name:          `Column (${el.tagName.toLowerCase()})`,
            parent:        sUid,
            order:         ord,
            systemDefined: false,
            fonts:         null,
            actions:       null,
            parameter:     null,
            properties: {
                desktop: {
                    visible:         true,
                    disableOptimize: false,
                    layout:    { transform: { translate: '0px,0px' }, width: '100%', height: '100%' },
                    container: { minWidth: isFullWidth ? 1200 : Math.round(rect.width), alignment: 'center' },
                    alignment: 'center',
                    background: null,
                },
                mobile: {
                    visible:         true,
                    disableOptimize: false,
                    layout:    { transform: { translate: '0px,0px' }, width: '100%', height: '100%' },
                    container: { minWidth: 375, alignment: null },
                    alignment: 'center',
                    stretched:  false,
                    background: null,
                },
            },
            content: null,
        });

        sectionMap.push({
            sectionElement:   el,
            sectionUid:       sUid,
            columnUid:        cUid,
            sectionRect:      rect,
            mobileSectionRect: mRect,
        });
    });

    return { sectionWidgets, columnWidgets, sectionMap };
}